/**
 * AddTab — Coin Onboarding + Architecture Reference + Coin Management
 * Sub-tabs: Add Coins | Architecture | Coin Management
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  fetchExistingCoins,
  startOnboarding,
  validateCoinsOnBinance,
  cancelOnboarding,
} from './api';
import { OnboardStatus, ExistingCoin } from './types';
import { useCoinRegistry } from '../../shared/CoinRegistryContext';
import { apiFetch } from '../../auth/AuthContext';
import ArchitectureTab from './ArchitectureTab';
import CoinManagementTab from './CoinManagementTab';
import './AddTab.css';

/** Fire-and-forget activity log to auth_server. */
function logActivity(
  action: string,
  target_id: string,
  target_label: string,
  metadata?: object,
) {
  const params = new URLSearchParams({
    action,
    target_id,
    target_label,
    metadata: JSON.stringify(metadata || {}),
  });
  apiFetch(`/activity/log?${params}`, { method: 'POST' }).catch(() => {});
}

export type AddTabView = 'add' | 'arch' | 'manage';

interface AddTabProps {
  onAdd?: unknown;
  /** Restrict which sub-tabs are visible. Defaults to all three. */
  allowedViews?: AddTabView[];
}

const ALL_VIEWS: AddTabView[] = ['add', 'arch', 'manage'];

const VIEW_LABELS: Record<AddTabView, string> = {
  add: 'Add Coins',
  arch: 'Architecture',
  manage: 'Coin Management',
};

/* ── Pipeline steps ── */
const STEPS = [
  { key: '5m', label: '5m OHLCV' },
  { key: '1m', label: '1m OHLCV' },
  { key: 'deriv', label: 'Derivatives' },
  { key: 'reg', label: 'Register' },
  { key: 'pipe', label: 'Pipeline' },
  { key: 'write', label: 'Write DB' },
];

function detectStep(logs: string[]): number {
  const t = logs.join('\n').toLowerCase();
  if (t.includes('preprocessing pipeline completed') || t.includes('write'))
    return 5;
  if (t.includes('pipeline_process_coin')) return 4;
  if (t.includes('register')) return 3;
  if (t.includes('derivat') || t.includes('funding')) return 2;
  if (t.includes('1m')) return 1;
  if (t.includes('5m') || t.includes('ohlcv')) return 0;
  return -1;
}

function parseLogLine(raw: string): { ts: string; level: string; msg: string } {
  const m = raw.match(
    /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}) \[(\w+)\]\s+(.*)$/,
  );
  if (m) return { ts: m[1].slice(11), level: m[2].toLowerCase(), msg: m[3] };
  return { ts: '', level: raw.startsWith('──') ? 'system' : 'info', msg: raw };
}

/** Parse tqdm progress output like "DOGEUSDT spot 5m:  45%|████| 450/1000 [01:30<00:10, 5.0page/s]" */
function parseTqdmProgress(
  line: string,
): { percent: number; eta: string } | null {
  const m = line.match(/(\d+)%\|[^|]*\|\s*[\d,]+\/[\d,]+\s*\[([^\]]*)\]/);
  if (!m) return null;
  const eta = m[2].includes('<') ? m[2].split('<')[1].split(',')[0].trim() : '';
  return { percent: parseInt(m[1]), eta };
}

const POPULAR = [
  'BTC',
  'ETH',
  'SOL',
  'AVAX',
  'ARB',
  'OP',
  'LINK',
  'MATIC',
  'ADA',
  'DOT',
  'NEAR',
  'INJ',
];

export default function AddTab({ allowedViews }: AddTabProps) {
  const views =
    allowedViews && allowedViews.length > 0
      ? ALL_VIEWS.filter((v) => allowedViews.includes(v))
      : ALL_VIEWS;
  const [subTab, setSubTab] = useState<AddTabView>(views[0]);

  // Keep subTab in sync if allowedViews changes and current view is no longer allowed.
  useEffect(() => {
    if (!views.includes(subTab)) setSubTab(views[0]);
  }, [views, subTab]);

  const { refreshRegistry, disabledCoins } = useCoinRegistry();

  /* ── state ── */
  const [chips, setChips] = useState<string[]>([]);
  const [inputVal, setInputVal] = useState('');
  const [status, setStatus] = useState<OnboardStatus>('idle');
  const [logs, setLogs] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [stepProgress, setStepProgress] = useState<
    Record<number, { percent: number; eta: string }>
  >({});
  const [validating, setValidating] = useState(false);
  const [invalidCoins, setInvalidCoins] = useState<string[]>([]);
  const [existing, setExisting] = useState<ExistingCoin[]>([]);
  const [loadingCoins, setLoadingCoins] = useState(true);
  const [catSearch, setCatSearch] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);
  const logEndRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const existingSet = new Set(existing.map((c) => c.symbol));

  const loadExisting = useCallback(async () => {
    setLoadingCoins(true);
    try {
      setExisting(await fetchExistingCoins());
    } catch {
      /* silent */
    } finally {
      setLoadingCoins(false);
    }
  }, []);

  useEffect(() => {
    loadExisting();
  }, [loadExisting]);
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const addChip = (raw: string) => {
    const val = raw.trim().toUpperCase();
    if (!val || chips.includes(val)) return;
    if (chips.length >= 5) {
      setError('Max 5 coins per batch');
      return;
    }
    setChips((p) => [...p, val]);
    setInputVal('');
    setError('');
  };

  const removeChip = (coin: string) =>
    setChips((p) => p.filter((c) => c !== coin));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', ',', ' '].includes(e.key)) {
      e.preventDefault();
      addChip(inputVal);
    } else if (e.key === 'Backspace' && !inputVal && chips.length)
      removeChip(chips[chips.length - 1]);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    e.clipboardData
      .getData('text')
      .split(/[\s,]+/)
      .filter(Boolean)
      .forEach(addChip);
  };

  const handleOnboard = async () => {
    const newCoins = chips.filter((c) => !existingSet.has(c));
    if (!newCoins.length) {
      setError('All coins are already onboarded.');
      return;
    }

    setValidating(true);
    setError('');
    setInvalidCoins([]);

    const map = await validateCoinsOnBinance(newCoins);
    const bad = newCoins.filter((c) => !map[c]);
    setValidating(false);

    if (bad.length) {
      setInvalidCoins(bad);
      setError(`Not found on Binance: ${bad.join(', ')}`);
      return;
    }

    setStatus('running');
    setLogs([]);
    setStepProgress({});

    // Activity log: onboard_start
    logActivity('onboard_start', newCoins.join(','), newCoins.join(', '), {
      coins: newCoins,
    });

    abortRef.current = startOnboarding(
      newCoins,
      undefined,
      (line) => {
        setLogs((p) => {
          const next = [...p, line];
          // Parse tqdm progress and update step progress bar
          const tqdm = parseTqdmProgress(line);
          if (tqdm) {
            const step = detectStep(next);
            if (step >= 0) {
              setStepProgress((prev) => ({ ...prev, [step]: tqdm }));
            }
          }
          return next;
        });
      },
      (ok) => {
        setStatus(ok ? 'success' : 'error');
        if (ok) {
          logActivity(
            'onboard_complete',
            newCoins.join(','),
            newCoins.join(', '),
            { coins: newCoins },
          );
          setChips([]);
          loadExisting();
          refreshRegistry();
        }
      },
    );
  };

  const handleCancel = async () => {
    const coinsToClean = chips.filter((c) => !existingSet.has(c));
    abortRef.current?.abort();
    setStatus('idle');
    setLogs((p) => [...p, '── Cancelling ──']);

    if (coinsToClean.length > 0) {
      logActivity(
        'onboard_cancel',
        coinsToClean.join(','),
        coinsToClean.join(', '),
        { coins: coinsToClean },
      );
      setLogs((p) => [
        ...p,
        `── Killing process and cleaning up: ${coinsToClean.join(', ')} ──`,
      ]);
      try {
        const result = await cancelOnboarding(coinsToClean);
        if (result.status === 'ok') {
          setLogs((p) => [
            ...p,
            result.killed
              ? '── Subprocess killed ──'
              : '── Subprocess already stopped ──',
            `── Cleaned ${result.cleaned?.join(', ')} from all 4 databases ──`,
            '── Removed from TOKENS list ──',
            '── Cancel complete ──',
          ]);
        } else {
          setLogs((p) => [...p, `── Cleanup warning: ${result.message} ──`]);
        }
      } catch {
        setLogs((p) => [
          ...p,
          '── Cancel request failed — subprocess may still be running ──',
        ]);
      }
      loadExisting();
      refreshRegistry();
    }
  };

  const currentStep = status === 'running' ? detectStep(logs) : -1;
  const dupeCoins = chips.filter((c) => existingSet.has(c));
  const newCount = chips.filter((c) => !existingSet.has(c)).length;
  const isRunning = status === 'running';
  const isBusy = isRunning || validating;

  const filteredExisting = existing
    .filter((c) => !disabledCoins.has(c.symbol))
    .filter((c) => !catSearch || c.symbol.includes(catSearch.toUpperCase()));

  const suggestions = POPULAR.filter(
    (p) => !existingSet.has(p) && !chips.includes(p),
  ).slice(0, 8);

  const statusLabel: Record<OnboardStatus, string> = {
    idle: 'Ready',
    running: 'Running',
    success: 'Completed',
    error: 'Failed',
  };

  const chipClass = (coin: string) => {
    if (invalidCoins.includes(coin)) return 'invalid';
    if (existingSet.has(coin)) return 'exists';
    return 'valid';
  };

  return (
    <div className="at-page">
      {/* ── Sub-tab bar ── */}
      {views.length > 1 && (
        <div className="at-subtab-bar">
          {views.map((v) => (
            <button
              key={v}
              className={`at-subtab ${subTab === v ? 'active' : ''}`}
              onClick={() => setSubTab(v)}
            >
              {VIEW_LABELS[v]}
            </button>
          ))}
        </div>
      )}

      {/* ══════════ ADD COINS VIEW ══════════ */}
      {subTab === 'add' && (
        <div className="at-root">
          <div className="at-left">
            <div className="at-card">
              <div className="at-card-header">
                <span className="at-card-title">Add Coins to Pipeline</span>
                <span
                  className={`at-status-pill ${validating ? 'validating' : status}`}
                >
                  {(isRunning || validating) && <span className="at-pulse" />}
                  {validating ? 'Validating' : statusLabel[status]}
                </span>
              </div>

              <div className="at-card-body">
                <div className="at-headline">Onboard New Coins</div>
                <div className="at-sub">
                  Pull historical OHLCV + derivatives from Binance and run the
                  full cleaning pipeline
                </div>

                <div className="at-input-label">Coin Symbols</div>
                <div
                  className="at-chip-wrap"
                  onClick={() => inputRef.current?.focus()}
                >
                  {chips.map((coin) => (
                    <span key={coin} className={`at-chip ${chipClass(coin)}`}>
                      {coin}
                      {!isBusy && (
                        <button
                          className="at-chip-remove"
                          onClick={() => removeChip(coin)}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  ))}
                  <input
                    ref={inputRef}
                    className="at-chip-input"
                    value={inputVal}
                    onChange={(e) => setInputVal(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    placeholder={
                      chips.length === 0
                        ? 'e.g. DOGE  SUI  KAT — Enter or Space to add'
                        : ''
                    }
                    disabled={isBusy}
                  />
                </div>

                <div className="at-hints">
                  <span className="at-hint">
                    Max 5 coins · Enter, Space, or comma to add · Backspace to
                    remove
                  </span>
                  {dupeCoins.length > 0 && (
                    <span className="at-warn">
                      {dupeCoins.join(', ')} already onboarded — will skip
                    </span>
                  )}
                </div>

                {error && (
                  <div className="at-err" style={{ marginTop: 8 }}>
                    {error}
                  </div>
                )}

                {suggestions.length > 0 && !isBusy && (
                  <>
                    <div className="at-quick-label">Quick Add</div>
                    <div className="at-quick-list">
                      {suggestions.map((sym) => (
                        <button
                          key={sym}
                          className="at-quick-btn"
                          disabled={chips.includes(sym) || chips.length >= 5}
                          onClick={() => addChip(sym)}
                        >
                          {sym}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                <div className="at-divider" />

                <div className="at-cta-row">
                  {!isRunning ? (
                    <button
                      className="at-btn-primary"
                      onClick={handleOnboard}
                      disabled={chips.length === 0 || isBusy}
                    >
                      {validating ? (
                        'Validating…'
                      ) : (
                        <>
                          Onboard
                          {newCount > 0 && (
                            <span className="at-count-badge">{newCount}</span>
                          )}
                        </>
                      )}
                    </button>
                  ) : (
                    <button className="at-btn-ghost" onClick={handleCancel}>
                      ✕ Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>

            {status !== 'idle' && (
              <div className="at-card">
                <div className="at-card-header">
                  <span className="at-card-title">Pipeline Progress</span>
                </div>
                <div className="at-card-body">
                  <div className="at-stepper">
                    {STEPS.map((step, i) => {
                      const cls =
                        status === 'error' && i === currentStep
                          ? 'error'
                          : i < currentStep
                            ? 'done'
                            : i === currentStep
                              ? 'active'
                              : '';
                      const prog = stepProgress[i];
                      return (
                        <div key={step.key} className={`at-step-item ${cls}`}>
                          <div className="at-step-dot">
                            {i < currentStep
                              ? '✓'
                              : i === currentStep && status === 'error'
                                ? '✕'
                                : i + 1}
                          </div>
                          <div className="at-step-label">{step.label}</div>
                          {prog != null &&
                            (i === currentStep || i < currentStep) && (
                              <div className="at-step-progress">
                                <div className="at-step-bar">
                                  <div
                                    className="at-step-bar-fill"
                                    style={{
                                      width: `${i < currentStep ? 100 : prog.percent}%`,
                                    }}
                                  />
                                </div>
                                <span className="at-step-pct">
                                  {i < currentStep
                                    ? '100%'
                                    : `${prog.percent}%`}
                                  {prog.eta && i === currentStep
                                    ? ` · ${prog.eta}`
                                    : ''}
                                </span>
                              </div>
                            )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {logs.length > 0 && (
              <div className="at-card">
                <div className="at-terminal-bar">
                  <div className="at-terminal-dots">
                    <div className="at-terminal-dot red" />
                    <div className="at-terminal-dot yellow" />
                    <div className="at-terminal-dot green" />
                  </div>
                  <span className="at-terminal-title">pipeline output</span>
                  {!isRunning && (
                    <button
                      className="at-terminal-clear"
                      onClick={() => setLogs([])}
                    >
                      clear
                    </button>
                  )}
                </div>
                <div className="at-log">
                  {logs.map((raw, i) => {
                    const { ts, level, msg } = parseLogLine(raw);
                    return (
                      <div key={i} className={`at-log-line ${level}`}>
                        {ts && <span className="at-log-ts">{ts}</span>}
                        <span className="at-log-msg">{msg}</span>
                      </div>
                    );
                  })}
                  <div ref={logEndRef} />
                </div>
              </div>
            )}
          </div>

          <div className="at-right">
            <div className="at-card">
              <div className="at-card-header">
                <span className="at-card-title">Coin Catalogue</span>
              </div>

              <div className="at-cat-stats" style={{ paddingTop: 16 }}>
                <div className="at-stat">
                  <span className="at-stat-val">
                    {loadingCoins ? '—' : existing.length}
                  </span>
                  <span className="at-stat-key">Onboarded</span>
                </div>
                <div className="at-stat">
                  <span className="at-stat-val">
                    {loadingCoins
                      ? '—'
                      : existing.reduce((a, c) => a + c.tables, 0)}
                  </span>
                  <span className="at-stat-key">Tables</span>
                </div>
              </div>

              <div className="at-search-wrap">
                <input
                  className="at-search"
                  placeholder="Search coins…"
                  value={catSearch}
                  onChange={(e) => setCatSearch(e.target.value)}
                  autoComplete="off"
                />
              </div>

              {!loadingCoins && (
                <div className="at-cat-grid">
                  {filteredExisting.map((c) => (
                    <span
                      key={c.symbol}
                      className={`at-cat-tag ${chips.includes(c.symbol) ? 'highlighted' : ''}`}
                    >
                      {c.symbol}
                    </span>
                  ))}
                  {filteredExisting.length === 0 && (
                    <span style={{ fontSize: '0.75rem', color: '#30363d' }}>
                      No match
                    </span>
                  )}
                </div>
              )}

              <div className="at-cat-divider" />

              <div className="at-cat-table-wrap">
                {loadingCoins ? (
                  <div className="at-cat-empty">Loading…</div>
                ) : filteredExisting.length === 0 ? (
                  <div className="at-cat-empty">No coins found</div>
                ) : (
                  <table className="at-cat-table">
                    <thead>
                      <tr>
                        <th>Symbol</th>
                        <th>Tables</th>
                        <th>Range</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredExisting.map((c) => (
                        <tr key={c.symbol}>
                          <td>{c.symbol}</td>
                          <td>{c.tables}</td>
                          <td className="at-range">
                            {c.earliest.slice(0, 10)} → {c.latest.slice(0, 10)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ ARCHITECTURE VIEW ══════════ */}
      {subTab === 'arch' && <ArchitectureTab coinCount={existing.length} />}

      {/* ══════════ COIN MANAGEMENT VIEW ══════════ */}
      {subTab === 'manage' && <CoinManagementTab />}
    </div>
  );
}
